import { ReactNode } from 'react';
import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';

const STATSIG_CLIENT_KEY = import.meta.env.VITE_STATSIG_CLIENT_KEY || '';
const STATSIG_ENVIRONMENT = import.meta.env.VITE_STATSIG_ENVIRONMENT || 'development';

interface StatsigBootstrapProps {
  children: ReactNode;
}

function StatsigEnabledProvider({ children }: StatsigBootstrapProps) {
  const { client } = useClientAsyncInit(
    STATSIG_CLIENT_KEY,
    { userID: 'anonymous' },
    {
      environment: { tier: STATSIG_ENVIRONMENT },
      plugins: [
        new StatsigAutoCapturePlugin({
          // Surface warn + error logs alongside the replay so we can debug
          // candidate-side issues (camera/upload/etc.) without asking for repro
          // steps. info/debug/log are intentionally skipped to keep payload size
          // and PII surface area down.
          consoleLogAutoCaptureSettings: {
            enabled: true,
            logLevel: 'warn',
            sampleRate: 1,
          },
        }),
        // The session replay plugin was removed: @jobtarget/browser-rum records
        // the session now, and two DOM recorders in one document both patch
        // fetch, XHR and console. Auto-capture analytics stay.
      ],
    }
  );

  return <StatsigProvider client={client}>{children}</StatsigProvider>;
}

function StatsigBootstrap({ children }: StatsigBootstrapProps) {
  if (!STATSIG_CLIENT_KEY) {
    return <>{children}</>;
  }
  return <StatsigEnabledProvider>{children}</StatsigEnabledProvider>;
}

export default StatsigBootstrap;
